import { getSkewCorrectedDate, maybeUpdateClockOffset } from '../clockOffset.js';
import { ClockSkewError } from '../errors/clockSkewError.js';

import type { FetchEnhancer } from '../types/fetchEnhancer.js';
import type { SigningEnhancerOptions } from './signing/signing.js';

/**
 * Clock skew enhancer autoatically adds the `date` header if the clients clock is sufficiently skewed from the server.
 *
 * Browser clients can produce the wrong date due to the system clock being set wrong.
 * The current date is a factor when signing so if it does not match up with the server time those requests get rejected.
 */
export const clockSkewEnhancer: FetchEnhancer<Partial<SigningEnhancerOptions>> =
  (fetch) => async (url, options) => {
    const request = new Request(url, options);

    // Preload credentials ahead of request in order to set the clock offset.
    // This is necessary for the signing in casae the clock is skewed.
    if (typeof options?.awsCredentials === 'function') {
      await options.awsCredentials();
    }
    request.headers.set('x-signing-date', getSkewCorrectedDate(new Date()).toUTCString());

    // Try to make the request
    const response = await fetch(request.url, {
      ...options,
      headers: request.headers,
    } as typeof options);

    if (!response.ok && response.status >= 400 && response.status < 500) {
      // If the server allows date header (Access-Control-Expose-Headers: date) we can use it to check if the clock is skewed
      const serverTime = response.headers.get('date');

      if (serverTime && maybeUpdateClockOffset(new Date(serverTime))) {
        const message = 'The request failed due to skewed clock';
        throw new ClockSkewError(message);
      }
    }

    return response;
  };
